import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import SongTable from '../components/SongTable';
import axios from 'axios';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, authToken, favorites, refreshFavorites } = useAuth();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [showReferrals, setShowReferrals] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    refreshFavorites();

    fetch('/api/referrals/my', {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((res) => res.json())
      .then((data) => setReferrals(data.referrals || []));
  }, [user, navigate, authToken]);

  useEffect(() => {
    const handleFocus = () => refreshFavorites();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshFavorites]);

  const queueSong = async (options) => {
    setQueueLoading(true);
    try {
      if (!authToken) {
        toast.error('You must be logged in to queue songs');
        setQueueLoading(false);
        return;
      }

      if (favorites.length === 0) {
        toast.error('You have no favorite songs to queue');
        setQueueLoading(false);
        return;
      }
      
      const response = await axios.post('/api/queue', 
        {
          from_favorites: true,
          ...options
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success(`✅ Queued: ${response.data.song_details.artist} - ${response.data.song_details.title}`);
      }
    } catch (error) {
      console.error('Failed to queue song:', error);
      toast.error(`❌ ${error.response?.data?.error || 'Failed to queue song'}`);
    } finally {
      setQueueLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const saveNewPassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwordData.newPassword.length < 4) {
      toast.error("New password must be at least 4 characters long");
      return;
    }

    setPasswordLoading(true);
    
    try {
      const response = await axios.put('/api/auth/change-password', 
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      toast.success("Password changed successfully");
      
      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Close the section
      setShowPasswordChange(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Failed to change password";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4"><img src="/emoji/1F464.svg" alt="Profile" className="inline w-5 h-5 mr-1 align-text-bottom" /> Profile</h1>
      <p className="mb-4">Username: <strong>{user?.username}</strong></p>
      
      {/* 🔒 Change Password */}
      <div className="mt-6">
        <button
          onClick={() => setShowPasswordChange((prev) => !prev)}
          className="text-xl font-semibold mb-2 flex items-center text-left"
        >
          <img src="/emoji/1F510.svg" alt="Password" className="inline w-5 h-5 mr-1 align-text-bottom" /> Change password
          <span className="ml-2 text-sm text-blue-600 underline">
            {showPasswordChange ? 'Hide' : 'Show'}
          </span>
        </button>

        {showPasswordChange && (
          <form onSubmit={saveNewPassword} className="border p-4 rounded shadow-sm space-y-4 max-w-md">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium mb-1">
                Current Password:
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium mb-1">
                New Password:
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={4}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
                Confirm New Password:
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <button
                type="submit"
                disabled={passwordLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded disabled:opacity-50"
              >
                {passwordLoading ? 'Saving...' : 'Save New Password'}
              </button>
            </div>
          </form>
        )}
      </div>
      
      {/* 🎟️ Referrals */}
      <div className="mt-6">
        <button
          onClick={() => setShowReferrals((prev) => !prev)}
          className="text-xl font-semibold mb-2 flex items-center text-left"
        >
          <img src="/emoji/1F39F.svg" alt="Referrals" className="inline w-5 h-5 mr-1 align-text-bottom" /> Your invite codes
          <span className="ml-2 text-sm text-blue-600 underline">
            {showReferrals ? 'Hide' : 'Show'}
          </span>
        </button>

        {showReferrals && (
          <ul className="space-y-2">
            {referrals.map((ref) => (
              <li key={ref.code} className="border p-3 rounded shadow-sm">
                <p>
                  <strong>Code:</strong>{' '}
                  {!ref.consumer_username ? (
                    <a
                      href={`https://hypr.website/signup#${ref.code}`}
                      className="text-blue-600 underline break-all"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ref.code}
                    </a>
                  ) : (
                    ref.code
                  )}
                </p>
                <p>
                  <strong>Status:</strong>{' '}
                  {ref.consumer_username
                    ? `Used by ${ref.consumer_username}`
                    : 'Unused'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ⭐ Favorites */}
      <h2 className="text-xl font-semibold mt-6 mb-2"><img src="/emoji/2B50.svg" alt="Favorites" className="inline w-5 h-5 mr-1 align-text-bottom" /> Favorite Songs</h2>
      
      {/* Queue buttons for favorites */}
      {favorites.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button 
            onClick={() => queueSong({ least_recent: true })}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
            disabled={queueLoading || !authToken || favorites.length === 0}
            title={!authToken ? "Login required" : favorites.length === 0 ? "No favorites" : ""}
          >
            {queueLoading ? 'Queueing...' : 'Least Recent'}
          </button>
          <button 
            onClick={() => queueSong({})}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
            disabled={queueLoading || !authToken || favorites.length === 0}
            title={!authToken ? "Login required" : favorites.length === 0 ? "No favorites" : ""}
          >
            {queueLoading ? 'Queueing...' : 'Random'}
          </button>
          <button 
            onClick={() => queueSong({ min_vote: 3 })}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
            disabled={queueLoading || !authToken || favorites.length === 0}
            title={!authToken ? "Login required" : favorites.length === 0 ? "No favorites" : ""}
          >
            {queueLoading ? 'Queueing...' : 'Favorite 3⭐+'}
          </button>
          <button 
            onClick={() => queueSong({ min_vote: 4 })}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
            disabled={queueLoading || !authToken || favorites.length === 0}
            title={!authToken ? "Login required" : favorites.length === 0 ? "No favorites" : ""}
          >
            {queueLoading ? 'Queueing...' : 'Favorite 4⭐+'}
          </button>
        </div>
      )}
      
      {favorites.length === 0 ? (
        <p className="text-gray-500">You have no favorite songs yet.</p>
      ) : (
        <SongTable
          songs={favorites}
          showTime={false}
          showFavorites={true}
          showRequest={true}
          disableRequestIf={(song) => song.recently_played}
          hideRequester={true}
          sortable={true}
        />
      )}
    </div>
  );
}
